import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { TrendingUp } from "lucide-react";

export default function Analytics() {
  const qc = useQueryClient();
  const { data: overview, refetch: refetchOverview } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/analytics/overview").then(r => r.data) });
  const { data: curve } = useQuery({ queryKey: ["retention-curve"], queryFn: () => api.get("/analytics/retention-curve").then(r => r.data) });
  const { data: platform = [] } = useQuery({ queryKey: ["platform-perf"], queryFn: () => api.get("/analytics/platform-performance").then(r => r.data) });
  const { data: top = [] } = useQuery({ queryKey: ["top-content"], queryFn: () => api.get("/analytics/top-content").then(r => r.data) });

  React.useEffect(() => {
    // Ensure fresh data whenever the analytics page mounts
    qc.invalidateQueries({ queryKey: ["overview"] });
    qc.invalidateQueries({ queryKey: ["retention-curve"] });
    qc.invalidateQueries({ queryKey: ["platform-perf"] });
    qc.invalidateQueries({ queryKey: ["top-content"] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = [
    { label: "Impressions", val: overview?.total_impressions, color: "#F5A524" },
    { label: "Reach", val: overview?.total_reach, color: "#00F0FF" },
    { label: "Likes", val: overview?.total_likes, color: "#17C964" },
    { label: "Shares", val: overview?.total_shares, color: "#F31260" },
  ];

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#141414] px-8 py-5 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Analytics & Learning</div>
        <h1 className="font-display font-bold text-2xl">Cross-channel performance</h1>
      </div>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#171717] border border-[#171717]">
          {cards.map(c => (
            <div key={c.label} className="bg-[#0a0a0a] p-6">
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mb-2">{c.label}</div>
              <div className="font-display font-bold text-3xl" style={{ color: c.color }}>{(c.val ?? 0).toLocaleString()}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 border border-[#171717] bg-[#0a0a0a] p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
              <div>
                <div className="font-display font-bold text-sm">Retention curve — aggregated</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Sampled from {curve?.sampled_content || 0} pieces</div>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={curve?.curve || []}>
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F5A524" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#F5A524" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#141414" strokeDasharray="3 3" />
                  <XAxis dataKey="pct" tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#222" />
                  <YAxis tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#222" />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #222", borderRadius: 2, fontSize: 12 }} labelStyle={{ color: "#F5A524" }} />
                  <Area type="monotone" dataKey="retention" stroke="#F5A524" fill="url(#grad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lg:col-span-5 border border-[#171717] bg-[#0a0a0a] p-5">
            <div className="flex items-center gap-2 mb-4">
              <div>
                <div className="font-display font-bold text-sm">Platform performance</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Impressions vs shares</div>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platform}>
                  <CartesianGrid stroke="#141414" strokeDasharray="3 3" />
                  <XAxis dataKey="platform" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#222" />
                  <YAxis tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#222" />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #222", borderRadius: 2, fontSize: 12 }} labelStyle={{ color: "#F5A524" }} />
                  <Bar dataKey="impressions" fill="#F5A524" />
                  <Bar dataKey="shares" fill="#00F0FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="border border-[#171717] bg-[#0a0a0a]">
          <div className="px-5 py-4 border-b border-[#171717]">
            <div className="font-display font-bold text-sm">Top content by virality score</div>
          </div>
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#171717] font-mono text-[10px] uppercase tracking-widest text-[#52525B]">
            <div className="col-span-5">Title</div>
            <div className="col-span-2">Platform</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Score</div>
            <div className="col-span-2 text-right">Hook</div>
          </div>
          {top.map(c => (
            <div key={c.id} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#141414] last:border-0 text-sm hover:bg-[#0f0f0f] transition-colors">
              <div className="col-span-5 truncate">{c.title}</div>
              <div className="col-span-2 font-mono text-xs uppercase text-[#00F0FF]">{c.platform}</div>
              <div className="col-span-2 font-mono text-[10px] uppercase text-[#A1A1AA]">{c.status}</div>
              <div className="col-span-1 text-right font-display font-bold text-[#F5A524]">{Math.round(c.virality_score)}</div>
              <div className="col-span-2 text-right font-mono text-xs text-[#17C964]">{Math.round(c.virality_breakdown?.hook_strength || 0)}</div>
            </div>
          ))}
          {top.length === 0 && <div className="p-10 text-center text-sm text-[#52525B]">No content yet.</div>}
        </div>
      </div>
    </div>
  );
}
